import { useState, useEffect } from 'react'
import { Menu, X } from 'lucide-react'

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeItem, setActiveItem] = useState('')

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
      
      // Highlight active section
      const sections = document.querySelectorAll('section[id]')
      const scrollPosition = window.scrollY + 100

      sections.forEach(section => {
        const sectionTop = section.offsetTop
        const sectionHeight = section.clientHeight
        const sectionId = section.getAttribute('id') || ''

        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveItem(sectionId.toUpperCase())
        }
      })
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = [
    { name: 'THE EQUATION', href: '#equation' },
    { name: 'LEADERSHIP', href: '#leadership' },
    { name: 'SERVICES', href: '#services' },
    { name: 'THE CORE', href: '#team' },
    { name: 'LEGACY', href: '#legacy' },
    { name: 'CAREERS', href: '#recruitment' },
  ]

  // Function to handle TALK TO Q button click
  const handleTalkToQClick = () => {
    // Find the Slide8Interface section (using #interface)
    const interfaceSection = document.getElementById('interface')
    
    if (interfaceSection) {
      // Close mobile menu if open
      setIsMenuOpen(false)
      
      // Scroll to the interface section
      interfaceSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      })
      
      // Update active item
      setActiveItem('INTERFACE')
    } else {
      // Fallback: If element not found, scroll to bottom
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      })
    }
  }

  return (
    <nav className={`
      fixed top-0 w-full z-50 transition-all duration-500 ease-out
      ${scrolled 
        ? 'bg-gray-900/95 backdrop-blur-xl py-2 shadow-2xl shadow-cyan-500/10 border-b border-cyan-500/20' 
        : 'bg-gradient-to-b from-gray-900/50 to-transparent py-4'
      }
    `}>
      {/* Full width container with edge-to-edge content */}
      <div className="w-full">
        <div className="flex items-center justify-between h-14 lg:h-16 px-4 sm:px-6 md:px-8">
          {/* Logo only - no text */}
          <a 
            href="#portal" 
            className="flex items-center group relative"
            onClick={(e) => {
              e.preventDefault()
              window.scrollTo({ top: 0, behavior: 'smooth' })
              setActiveItem('')
            }}
          >
            <div className="relative">
              {/* Glow effect on hover */}
              <div className="absolute inset-0 opacity-0 transition-opacity duration-500"></div>
              
              {/* Logo image container - INCREASED SIZE */}
              <div className="relative w-44 h-20 lg:w-42 lg:h-17 flex items-center justify-center 
                            group-hover:scale-105 transition-all duration-500 
                            shadow-lg shadow-cyan-400/20 group-hover:shadow-cyan-400/40
                            overflow-hidden">
                {/* Logo Image */}
                <img 
                  src="/assets/images/logos/quavrion-logo.png" 
                  alt="Quavrion Logo"
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    // Fallback in case image fails to load
                    const target = e.target ;
                    console.error('Failed to load logo image');
                    target.style.display = 'none';
                    // Show text fallback
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `
                        <div class="flex items-center space-x-2">
                          <span class="text-cyan-400 font-bold text-2xl lg:text-3xl">Q</span>
                          <span class="text-white font-bold text-2xl lg:text-3xl">QUAVRION</span>
                        </div>
                      `;
                    }
                  }}
                />
              </div>
            </div>
          </a>

          {/* Desktop Navigation - Full width justified */}
          <div className="hidden lg:flex items-center justify-center flex-1 mx-12">
            <div className="flex items-center space-x-1 xl:space-x-2 max-w-4xl">
              {navItems.map((item) => {
                const isActive = activeItem === item.name.replace(' ', '_')
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault()
                      const section = document.querySelector(item.href)
                      if (section) {
                        section.scrollIntoView({ behavior: 'smooth' })
                        setActiveItem(item.name)
                      }
                    }}
                    className="group relative px-4 py-2 mx-1 flex-1 text-center min-w-[120px] transition-all duration-300"
                  >
                    <span className={`
                      relative z-10 font-medium text-sm xl:text-base tracking-wide whitespace-nowrap
                      transition-all duration-300 group-hover:tracking-wider
                      ${isActive ? 'text-cyan-400' : 'text-gray-300 hover:text-white'}
                    `}>
                      {item.name}
                    </span>
                    
                    {/* Always show underline on hover for all items */}
                    <div className="absolute bottom-0 left-0 w-full h-0.5">
                      {/* Active underline - shows when item is active */}
                      {isActive && (
                        <>
                          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-teal-400 rounded-full"></div>
                          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-teal-400 rounded-full 
                                        animate-pulse blur-[1px] opacity-70"></div>
                        </>
                      )}
                      
                      {/* Hover underline - shows on hover for ALL items (including active ones) */}
                      <div className={`
                        absolute left-1/2 transform -translate-x-1/2 
                        w-0 h-0.5 bg-gradient-to-r from-cyan-400 to-teal-400 rounded-full
                        group-hover:w-3/4 transition-all duration-500 ease-out
                        ${isActive ? 'opacity-50' : ''}
                      `}></div>
                    </div>
                  </a>
                )
              })}
            </div>
          </div>

          {/* CTA Button with advanced effects - Right edge aligned */}
          <div className="hidden lg:flex items-center">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-teal-400 rounded-lg blur opacity-30 
                            group-hover:opacity-70 group-hover:scale-105 transition-all duration-500"></div>
              <button 
                onClick={handleTalkToQClick}
                className="relative px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-teal-500 text-gray-900 font-bold rounded-lg 
                         text-sm xl:text-base tracking-wider whitespace-nowrap
                         hover:from-cyan-400 hover:to-teal-400 hover:scale-105 
                         active:scale-95
                         transition-all duration-300 transform
                         shadow-lg shadow-cyan-500/25 group-hover:shadow-cyan-500/40"
              >
                TALK TO Q
                <div className="absolute inset-0 rounded-lg border border-white/30 opacity-0 
                              group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-white/0 via-white/10 to-white/0 
                              opacity-0 group-hover:opacity-100 transition-opacity duration-500
                              group-hover:animate-shimmer"></div>
              </button>
            </div>
          </div>

          {/* Mobile Menu Button - Right edge aligned */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden relative w-12 h-12 flex items-center justify-center rounded-lg 
                     bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700
                     hover:border-cyan-400/50 hover:shadow-cyan-400/20 hover:shadow-lg
                     transition-all duration-300 shadow-lg group"
          >
            <div className={`absolute inset-0 bg-gradient-to-r from-cyan-400/10 to-teal-400/10 rounded-lg 
                          ${isMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'} transition-opacity duration-300`}></div>
            {isMenuOpen ? (
              <X size={26} className="text-cyan-400 relative z-10 group-hover:scale-110 transition-transform duration-300" />
            ) : (
              <Menu size={26} className="text-gray-300 group-hover:text-cyan-400 group-hover:scale-110 relative z-10 transition-all duration-300" />
            )}
          </button>
        </div>

        {/* Mobile Navigation - Full screen width */}
        <div className={`
          lg:hidden fixed left-0 right-0 top-35
          transition-all duration-500 ease-in-out
          ${isMenuOpen 
            ? 'opacity-100 translate-y-0 visible' 
            : 'opacity-0 -translate-y-4 invisible'
          }
        `}>
          <div className="mx-0 rounded-none bg-gradient-to-b from-gray-900 to-gray-950 
                        border-b border-cyan-500/20 shadow-2xl shadow-cyan-500/10
                        backdrop-blur-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-teal-500/5"></div>
            
            <div className="relative py-2 space-y-1">
              {navItems.map((item, index) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault()
                    setIsMenuOpen(false)
                    const section = document.querySelector(item.href)
                    if (section) {
                      section.scrollIntoView({ behavior: 'smooth' })
                      setActiveItem(item.name)
                    }
                  }}
                  className={`
                    group flex items-center px-6 py-4 mx-0
                    transition-all duration-300 transform active:scale-[0.98]
                    ${activeItem === item.name.replace(' ', '_')
                      ? 'text-cyan-400'
                      : 'text-gray-300 hover:text-white'
                    }
                    border-b border-gray-800/50 last:border-b-0
                    relative
                  `}
                  style={{transitionDelay: `${index * 50}ms`}}
                >
                  {/* Mobile active indicator - only left line */}
                  <div className={`w-1 h-6 mr-4 rounded-full bg-gradient-to-b from-cyan-400 to-teal-400 
                                transition-all duration-300
                                ${activeItem === item.name.replace(' ', '_') 
                                  ? 'opacity-100 scale-y-100' 
                                  : 'opacity-0 scale-y-0 group-hover:opacity-50 group-hover:scale-y-100'
                                }`}></div>
                  
                  <span className="font-medium text-base tracking-wide flex-1 
                                 transition-all duration-300 group-hover:tracking-wider">
                    {item.name}
                  </span>
                  
                  {/* Mobile active dot */}
                  <div className="ml-auto">
                    <div className={`w-2 h-2 rounded-full bg-cyan-400 
                                  transition-all duration-300
                                  ${activeItem === item.name.replace(' ', '_') 
                                    ? 'opacity-100 animate-pulse' 
                                    : 'opacity-0 group-hover:opacity-50'
                                  }`}></div>
                  </div>
                  
                  {/* Mobile underline on hover */}
                  {activeItem !== item.name.replace(' ', '_') && (
                    <div className="absolute bottom-0 left-6 right-6 h-0.5 
                                  bg-gradient-to-r from-cyan-400/0 via-cyan-400/0 to-teal-400/0
                                  group-hover:from-cyan-400/50 group-hover:via-cyan-400 group-hover:to-teal-400/50
                                  transition-all duration-500"></div>
                  )}
                </a>
              ))}
              
              {/* Mobile CTA Button - Full width */}
              <div className="px-6 py-4">
                <div className="relative group active:scale-[0.98] transition-transform duration-200">
                  <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-teal-400 rounded-xl blur opacity-30 
                                group-active:opacity-60 transition duration-300"></div>
                  <button 
                    onClick={handleTalkToQClick}
                    className="relative w-full px-6 py-3.5 bg-gradient-to-r from-cyan-500 to-teal-500 
                             text-gray-900 font-bold rounded-xl text-base
                             active:scale-95 transition-transform duration-200
                             shadow-lg shadow-cyan-500/25"
                  >
                    TALK TO Q
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add shimmer animation keyframes */}
      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: -200% center;
          }
          100% {
            background-position: 200% center;
          }
        }
        .animate-shimmer {
          animation: shimmer 3s infinite linear;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.1) 50%,
            transparent 100%
          );
          background-size: 200% 100%;
        }
      `}</style>
    </nav>
  )
}

export default Navbar